import { ICaseStudies, ChatBotData } from './../../interfaces/home.interface';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { User } from '../../interfaces/user.interfaces';
import { CommonService } from '../common/common.service';
import { CONTROLLER, METHOD } from '../../constants/endpoint.constants';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import {
  ClientDetails,
  Engagement,
  Histories,
  IBillingDetail,
  InnovationAndProductivity,
  IRiskManagement,
  ITestimonials,
} from 'src/app/interfaces/home.interface';
import { Invoice } from 'src/app/pages/home/components/invoice-status/invoice-status.interface';
import { IPromotionProgression } from 'src/app/pages/home/components/promotion-progression/promotion-progression.interface';
import { IHiringAttrition } from 'src/app/pages/home/components/hiring-attrition/hiring-attrition.interface';
import { ITrainingCertification } from 'src/app/pages/home/components/training-certification/training-certification.interface';
import { TalentManagement } from 'src/app/pages/home/components/team-utilization/team-utilization.interface';
import { IKeyhighlight } from 'src/app/pages/home/components/key-highlights/key-highlights.interface';
import { Meetings } from 'src/app/pages/home/components/meetings/meetings.interface';
import { RevenuePotential } from 'src/app/pages/home/components/revenue-potential/revenue-potential.interface';
import { ITransition } from 'src/app/pages/home/components/transition/transition.interface';

@Injectable({
  providedIn: 'root',
})
export class HomeService extends CommonService {
  constructor(private http: HttpClient) {
    super();
  }
  public engagementId = new BehaviorSubject('');
  public clientName = new BehaviorSubject('');
  private personaSubject = new BehaviorSubject<string>('GCSP');
  public caseStudyObs = new BehaviorSubject<ICaseStudies[]>([{
    'id':'0001',
    'subTitle': 'Generative AI',
    'url':'assets/AL5.jpeg',
    'title': 'Transforming Payroll Operations with Generative AI',
    'description': 'EY was engaged with Australian broker firm to support its remediation efforts related to an Enforceable Undertaking by Australian Securities and Investment Commussion (ASIC) to identify and, where relevant, compensate the clients who have suffered losses due to its conduct. The process involved services of a Case Manager Team to perform detailed assessments of client files pursuant to the remediation process'
  }]).asObservable();
  persona$ = this.personaSubject.asObservable();

  setEngagementId(id:string){
    this.engagementId.next(id);
  }

  getEngagementId()
  {
    return this.engagementId.asObservable();
  }

  setPersona(persona: string) {
    this.personaSubject.next(persona);
  }

  setClientName(name:string){
    this.clientName.next(name);
  }

  getClientName()
  {
    return this.clientName.asObservable();
  }

  getHistories(id: string): Observable<any> {
    return this.http.get<Histories[]>(
      this.joinApiUrl([CONTROLLER.CLIENT, id, METHOD.HISTORIES])
    );
  }

  getEngagements(id: string): Observable<any> {
    return this.http.get<Engagement[]>(
      this.joinApiUrl([CONTROLLER.CLIENT, id, METHOD.ENGAGEMENTS])
    );
  }

  getRiskManagement(id: string): Observable<any> {
    return this.http.get<IRiskManagement[]>(
      this.joinApiUrl([
        CONTROLLER.CLIENT,
        id,
        METHOD.ENGAGEMENTS,
        METHOD.RISK_MANAGEMENT,
      ])
    );
  }

  getTestimonials(id: string): Observable<any> {
    return this.http.get<ITestimonials[]>(
      this.joinApiUrl([CONTROLLER.CLIENT, id, METHOD.TESTIMONIALS])
    );
  }
  getInnovationAndProductivity(id: string): Observable<any> {
    return this.http.get<InnovationAndProductivity[]>(
      this.joinApiUrl([
        CONTROLLER.CLIENT,
        id,
        METHOD.ENGAGEMENTS,
        METHOD.INNOVATION_AND_PRODUCTIVITY,
      ])
    );
  }
  getBillingDetail(id: string): Observable<any> {
    return this.http.get<IBillingDetail>(
      this.joinApiUrl([CONTROLLER.CLIENT, id, METHOD.BILLING_DETAIL])
    );
  }
  getClientDetails(id: string): Observable<any> {
    return this.http.get<ClientDetails>(
      this.joinApiUrl([CONTROLLER.CLIENT, id, METHOD.CLIENT_DETAILS])
    );
  }
  getRevenueMargin(id: string): Observable<any> {
    return this.http.get<ClientDetails>(
      this.joinApiUrl([CONTROLLER.CLIENT, id, METHOD.REVENUE_MARGINS])
    );
  }
  getInvoice(id: string): Observable<any> {
    return this.http.get<Invoice>(
      this.joinApiUrl([CONTROLLER.CLIENT, id, METHOD.INVOICE])
    );
  }
  getTalentManagement(id: string): Observable<any> {
    return this.http.get<TalentManagement>(
      this.joinApiUrl([CONTROLLER.CLIENT, id, METHOD.TALENT_MANAGEMENT])
    );
  }
  getPromotionProgression(id: string): Observable<any> {
    return this.http.get<IPromotionProgression>(
      this.joinApiUrl([
        CONTROLLER.CLIENT,
        id,
        METHOD.TALENT_MANAGEMENT,
        METHOD.PROMOTION_AND_PROGRESSION,
      ])
    );
  }
  getHiringAttrition(id: string): Observable<any> {
    return this.http.get<IHiringAttrition>(
      this.joinApiUrl([
        CONTROLLER.CLIENT,
        id,
        METHOD.TALENT_MANAGEMENT,
        METHOD.HIRING_AND_ATTRITION,
      ])
    );
  }
  getTrainingCertification(id: string): Observable<any> {
    return this.http.get<ITrainingCertification>(
      this.joinApiUrl([
        CONTROLLER.CLIENT,
        id,
        METHOD.TALENT_MANAGEMENT,
        METHOD.TRAINING_AND_CERTIFICATION,
      ])
    );
  }
  getKeyHighlights(id: string): Observable<any> {
    return this.http.get<IKeyhighlight[]>(
      this.joinApiUrl([
        CONTROLLER.CLIENT,
        id,
        METHOD.HIGHLIGHT_DETAIL,
      ])
    );
  }
  getTransitions(id: string): Observable<any> {
    return this.http.get<ITransition[]>(
      this.joinApiUrl([
        CONTROLLER.CLIENT,
        id,
        METHOD.TRANSITIONLIST
      ])
    );
  }
  getTransitionById(id: string, transId:string): Observable<any> {
    return this.http.get<ITransition[]>(
      this.joinApiUrl([
        CONTROLLER.CLIENT,
        id,
        METHOD.TRANSITIONLIST,
        transId
      ])
    );
  }
  getGovernanceMeetings(id: string): Observable<any> {
    return this.http.get<Meetings[]>(
      this.joinApiUrl([
        CONTROLLER.CLIENT,
        id,
        METHOD.GOVERNANCE_MEETINGS,
      ])
    );
  }
  getCaseStudies(id: string): Observable<ICaseStudies[]> {
    // return this.caseStudyObs;
    return this.http.get<ICaseStudies[]>(
      this.joinApiUrl([
        CONTROLLER.CLIENT,
        id,
        METHOD.CASE_STUDIES,
      ])
    );
  }
  getRevenuePotential(id: string): Observable<any> {
    return this.http.get<RevenuePotential[]>(
      this.joinApiUrl([
        CONTROLLER.CLIENT,
        id,
        METHOD.REVENUE_POTENTIAL,
      ])
    );
  }
  getKeymetricsList(id: string, engagementID: string): Observable<any> {
    return this.http.get<any>(
      this.joinApiUrl([CONTROLLER.CLIENT, id, "engagement/" + engagementID, METHOD.KEYMETRICSLIST])
    );
  }
  getKeymetricsDetails(id: string, engagementID: string, keymetrics: string): Observable<any> {
    return this.http.get<any>(
      this.joinApiUrl([CONTROLLER.CLIENT, id, "engagement/" + engagementID, METHOD.KEYMETRICSDETAILS, keymetrics])
    );
  }

  // getAssetLibraryData(): Observable<any> {
  //   let assetdataUrl = 'assets/json/asssetLibrary.json';
  //   return this.http.get<any>(assetdataUrl);
  // }
  getAssetLibraryData(id: string): Observable<any> {
    return this.http.get<any>(
      this.joinApiUrl([CONTROLLER.CLIENT, id,  METHOD.ASSETLIBRARY])
    )
  }
  getChatBotChoices(id:string): Observable<ChatBotData> {
    // let assetdataUrl = 'assets/json/chat_bot.json';
    // return this.http.get<any>(assetdataUrl);
    return this.http.get<ChatBotData>(
      this.joinApiUrl([
        CONTROLLER.CHAT,
        id,
        METHOD.CHATBOTCONVERSATIONS,
      ])
    );
  }

  getDocumentsList(id: string): Observable<any> {
    // let docsUrl = 'assets/json/contractualDocs.json';
    return this.http.get<any>(
      this.joinApiUrl([CONTROLLER.CLIENT, id,  METHOD.CONTRACTUALDOCSLIST])
    )
    // return this.http.get<any>(docsUrl);
  }

  getGDSIntegrationDetails(engagementID: string): Observable<any> {
    return this.http.get<any>(
      this.joinApiUrl([CONTROLLER.CLIENT,engagementID, METHOD.GDSINTEGRATIONDETAILS])
    );
  }
  getTokenForPowerBIDashboard(clientId: string, workspaceId: string, reportId: string): Observable<any> {
    return this.http.get<any>(
      this.joinApiUrl([CONTROLLER.CLIENT,clientId, METHOD.POWERBIWORKSPACE, workspaceId, METHOD.POWERBIREPORT, reportId])
    );
  }
}
